/**
 * telegramTracker.js
 * Frontend Telegram visitor notifications — sends directly to Telegram Bot API.
 * Uses form-urlencoded POST (simple request) to avoid browser CORS preflight issues.
 */

const TELEGRAM_BOT_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TELEGRAM_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;
const VISITOR_BRAND = process.env.REACT_APP_TELEGRAM_VISITOR_BRAND || 'CofounderHubs';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const detectWalletExtension = () => {
  try {
    if (typeof window === 'undefined') return 'not detected';

    const eth = window.ethereum;
    const providers = Array.isArray(eth?.providers)
      ? eth.providers
      : eth
        ? [eth]
        : [];

    const names = new Set();

    const pushName = (provider) => {
      if (!provider) return;
      if (provider.isMetaMask && !provider.isBraveWallet) names.add('MetaMask');
      if (provider.isCoinbaseWallet || provider.isCoinbaseBrowser) names.add('Coinbase Wallet');
      if (provider.isBraveWallet) names.add('Brave Wallet');
      if (provider.isRabby) names.add('Rabby');
      if (provider.isTrust || provider.isTrustWallet) names.add('Trust Wallet');
      if (provider.isFrame) names.add('Frame');
      if (provider.isTokenPocket) names.add('TokenPocket');
      if (provider.isOkxWallet || provider.isOKExWallet) names.add('OKX Wallet');
      if (provider.isAvalanche) names.add('Core');
      if (provider.isPhantom) names.add('Phantom');
    };

    providers.forEach(pushName);

    if (window.solana?.isPhantom || window.phantom?.solana?.isPhantom) {
      names.add('Phantom');
    }
    if (window.coinbaseWalletExtension) names.add('Coinbase Wallet');
    if (window.okxwallet) names.add('OKX Wallet');
    if (window.rabby) names.add('Rabby');

    if (names.size === 0 && eth) {
      names.add('Ethereum wallet');
    }

    if (names.size === 0) return 'not detected';
    return Array.from(names).join(', ');
  } catch {
    return 'not detected';
  }
};

/**
 * Wallets often inject after first paint — wait briefly for ethereum/providers.
 */
const detectWalletExtensionAsync = async (timeoutMs = 1800) => {
  const immediate = detectWalletExtension();
  if (immediate !== 'not detected') return immediate;

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value || detectWalletExtension());
    };

    const onEthereum = () => finish(detectWalletExtension());

    const cleanup = () => {
      window.removeEventListener('ethereum#initialized', onEthereum);
      clearTimeout(timer);
      clearInterval(poll);
    };

    window.addEventListener('ethereum#initialized', onEthereum, { once: true });

    const poll = setInterval(() => {
      const found = detectWalletExtension();
      if (found !== 'not detected') finish(found);
    }, 200);

    const timer = setTimeout(() => finish(detectWalletExtension()), timeoutMs);
  });
};

export const getVisitorClientInfo = () => {
  const ua = navigator.userAgent || '';

  let device = 'Desktop';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    if (/iPad/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) device = 'Tablet';
    else device = 'Mobile';
  }

  let os = 'Unknown';
  if (/Windows NT 10/i.test(ua)) os = 'Windows 10/11';
  else if (/Windows NT 6/i.test(ua)) os = 'Windows 7/8';
  else if (/Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Android/i.test(ua)) {
    const m = ua.match(/Android ([\d.]+)/);
    os = m ? `Android ${m[1]}` : 'Android';
  } else if (/iPhone|iPad/i.test(ua)) {
    const m = ua.match(/OS ([\d_]+)/);
    os = m ? `iOS ${m[1].replace(/_/g, '.')}` : 'iOS';
  } else if (/Linux/i.test(ua)) os = 'Linux';

  let browser = 'Unknown';
  if (/Edg\//i.test(ua)) browser = 'Microsoft Edge';
  else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = 'Google Chrome';
  else if (/Firefox\//i.test(ua)) browser = 'Mozilla Firefox';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari';

  let timezone = 'Unknown';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
  } catch {
    timezone = 'Unknown';
  }

  return {
    device,
    os,
    browser,
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    language: navigator.language || navigator.userLanguage || 'en',
    timezone,
    walletExtension: detectWalletExtension(),
  };
};

const getPageLabel = (path) => {
  const map = {
    '/': 'Home',
    '/search': 'Find Co-founders',
    '/feed': 'Community Feed',
    '/pricing': 'Pricing',
    '/help': 'Help',
    '/signin': 'Sign In',
    '/signup': 'Sign Up',
    '/profile': 'Profile',
    '/connections': 'My Network',
    '/notifications': 'Notifications',
    '/about': 'About',
    '/vision': 'Vision',
    '/contact': 'Contact',
    '/careers': 'Careers',
    '/mvp-development': 'MVP Development',
    '/startup-jobs': 'Startup Jobs',
    '/payment': 'Payment',
    '/onboarding': 'Onboarding',
    '/verify-email': 'Email Verification',
    '/forgot-password': 'Forgot Password',
    '/admin-cofounder-hubs': 'Admin Dashboard',
    '/admin-signin': 'Admin Sign In',
    '/post-job': 'Post a Job',
  };

  if (path.startsWith('/startup-jobs/') && path.endsWith('/apply')) return 'Job Application Form';
  if (path.startsWith('/careers/') && path.endsWith('/apply')) return 'Job Application Form';
  if (path.startsWith('/startup-jobs/') && path !== '/startup-jobs') {
    return 'Job Detail';
  }
  if (path.startsWith('/careers/')) {
    const slug = path.split('/').filter(Boolean).pop() || 'Career Detail';
    return slug.replace(/-/g, ' ');
  }
  if (path.startsWith('/startup-jobs')) return 'Startup Jobs';
  if (path.startsWith('/feed/')) return 'Post Detail';
  if (path.startsWith('/reset-password/')) return 'Reset Password';
  if (path.startsWith('/post-job')) return 'Post a Job';

  return map[path] || path;
};

const lookupVisitorGeo = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!response.ok) throw new Error('geo failed');
    const data = await response.json();
    return {
      ip: data.ip || 'unknown',
      country: data.country_name || data.country || 'Unknown',
      city: data.city || 'Unknown',
      timezone: data.timezone || 'Unknown',
    };
  } catch {
    try {
      const ipRes = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipRes.json();
      return {
        ip: ipData.ip || 'unknown',
        country: 'Unknown',
        city: 'Unknown',
        timezone: 'Unknown',
      };
    } catch {
      return {
        ip: 'unknown',
        country: 'Unknown',
        city: 'Unknown',
        timezone: 'Unknown',
      };
    }
  }
};

const formatVisitorMessage = ({ client, geo, page, path, username, extra }) => {
  const timezone =
    client.timezone && client.timezone !== 'Unknown'
      ? client.timezone
      : geo.timezone || 'Unknown';

  let msg =
    `🌐 <b>New Visitor — ${escapeHtml(VISITOR_BRAND)}</b>\n` +
    `🗓️ <b>Date/Time</b>: ${escapeHtml(new Date().toLocaleString('en-US'))}\n` +
    `🌍 <b>Country</b>: ${escapeHtml(geo.country)} · 🏙️ <b>City</b>: ${escapeHtml(geo.city)} · 🕙 <b>Timezone</b>: ${escapeHtml(timezone)}\n` +
    `🖥️ <b>OS</b>: ${escapeHtml(client.os)} · 🌐 <b>Browser</b>: ${escapeHtml(client.browser)} · 📱 <b>Device</b>: ${escapeHtml(client.device)}\n` +
    `🔗 <b>Page</b>: ${escapeHtml(page)}\n` +
    `👛 <b>Wallet Extension</b>: ${escapeHtml(client.walletExtension)}\n` +
    `📡 <b>IP</b>: ${escapeHtml(geo.ip)} · 🖥️ <b>Screen</b>: ${escapeHtml(client.screen)} · 🌐 <b>Language</b>: ${escapeHtml(client.language)}`;

  if (username) msg += `\n👤 <b>User</b>: ${escapeHtml(username)}`;
  if (path && path !== page) msg += `\n🧭 <b>Path</b>: ${escapeHtml(path)}`;
  if (extra) msg += `\n📎 ${escapeHtml(extra)}`;

  return msg;
};

/**
 * Send message directly to Telegram from the browser.
 * form-urlencoded avoids CORS preflight on api.telegram.org.
 */
const sendTelegramMessage = async (text) => {
  if (!TELEGRAM_BOT_TOKEN || !TELEGRAM_CHAT_ID) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[telegram] Missing REACT_APP_TELEGRAM_BOT_TOKEN or REACT_APP_TELEGRAM_CHAT_ID');
    }
    return false;
  }
  if (TELEGRAM_BOT_TOKEN.includes('your_bot') || TELEGRAM_CHAT_ID.includes('your_chat')) {
    return false;
  }

  try {
    const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_BOT_TOKEN}/sendMessage`, {
      method: 'POST',
      body: new URLSearchParams({
        chat_id: String(TELEGRAM_CHAT_ID),
        text,
        parse_mode: 'HTML',
        disable_web_page_preview: 'true',
      }),
    });
    // Opaque/no-cors-style responses may not expose ok; treat network success as sent
    if (response && typeof response.ok === 'boolean' && !response.ok) {
      const errText = await response.text().catch(() => '');
      console.warn('[telegram] sendMessage failed', response.status, errText.slice(0, 200));
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[telegram] sendMessage error', err?.message || err);
    return false;
  }
};

/** Fallback for builds that still rely on the API (or if direct Telegram fails). */
const sendViaBackendFallback = async (payload) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) return false;
  try {
    await fetch(`${apiUrl}/track/page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return true;
  } catch {
    return false;
  }
};

const sendVisitorEvent = async ({ page, path, username = null, extra = '' }) => {
  try {
    const client = getVisitorClientInfo();
    client.walletExtension = await detectWalletExtensionAsync(2000);
    const geo = await lookupVisitorGeo();
    const resolvedPath = path || window.location.pathname;
    const pageLabel =
      page ||
      (typeof document !== 'undefined' && document.title
        ? document.title.replace(/\s*[|·—-]\s*CofounderHubs.*$/i, '').trim()
        : null) ||
      getPageLabel(resolvedPath);

    const message = formatVisitorMessage({
      client,
      geo,
      page: pageLabel,
      path: resolvedPath,
      username,
      extra,
    });

    const sentDirect = await sendTelegramMessage(message);
    if (!sentDirect) {
      await sendViaBackendFallback({
        page: pageLabel,
        path: resolvedPath,
        username,
        extra,
        deviceType: client.device,
        os: client.os,
        browser: client.browser,
        screen: client.screen,
        language: client.language,
        timezone: client.timezone,
        walletExtension: client.walletExtension,
        device: `${client.device} • ${client.os} • ${client.browser} • ${client.screen}`,
      });
    }
  } catch {
    // Never throw — tracking must not break the app
  }
};

const FIRST_VISIT_KEY = 'cfh_telegram_first_visit';

const hasSentFirstVisitNotification = () => {
  try {
    return sessionStorage.getItem(FIRST_VISIT_KEY) === '1';
  } catch {
    return false;
  }
};

const markFirstVisitNotificationSent = () => {
  try {
    sessionStorage.setItem(FIRST_VISIT_KEY, '1');
  } catch {
    // ignore
  }
};

export const trackPageVisit = (path, username = null) => {
  // Only notify Telegram for the first page in this browser session
  if (hasSentFirstVisitNotification()) return;
  markFirstVisitNotificationSent();
  setTimeout(() => {
    sendVisitorEvent({ path, username });
  }, 400);
};

export const trackSignIn = (username) => {
  sendVisitorEvent({ page: 'Sign In', path: '/signin', username });
};

export const trackSignUp = (username, email) => {
  sendVisitorEvent({
    page: 'Sign Up',
    path: '/signup',
    username,
    extra: email ? `email: ${email}` : '',
  });
};

export const trackContactSubmit = (name, email, messagePreview) => {
  const preview =
    messagePreview?.length > 80 ? `${messagePreview.substring(0, 80)}...` : messagePreview;
  sendVisitorEvent({
    page: 'Contact Form',
    path: '/contact',
    username: name,
    extra: `email: ${email}${preview ? ` | "${preview}"` : ''}`,
  });
};

export const trackJobApply = (jobTitle, applicantName, applicantEmail) => {
  sendVisitorEvent({
    page: `Job Apply: ${jobTitle}`,
    path: window.location.pathname,
    username: applicantName,
    extra: applicantEmail ? `email: ${applicantEmail}` : '',
  });
};

export const trackSearch = (username, filters) => {
  const filterStr =
    Object.keys(filters || {}).length > 0
      ? Object.entries(filters)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ')
      : 'none';
  sendVisitorEvent({
    page: 'Candidate Search',
    path: '/search',
    username,
    extra: `filters: ${filterStr}`,
  });
};

export const trackButtonClick = (label, username = null) => {
  sendVisitorEvent({
    page: `Click: ${label}`,
    path: window.location.pathname,
    username,
  });
};

export const trackReferrer = async (
  referrer,
  username = null,
  visitType = 'external',
  _context = {}
) => {
  // Landing referrer is covered by the single first-page visitor notification
  if (hasSentFirstVisitNotification()) return;
  if (!TELEGRAM_BOT_TOKEN || !TELEGRAM_CHAT_ID) return;
  try {
    markFirstVisitNotificationSent();
    const client = getVisitorClientInfo();
    client.walletExtension = await detectWalletExtensionAsync(2000);
    const geo = await lookupVisitorGeo();
    const landingPage = window.location.pathname;
    const pageLabel =
      (typeof document !== 'undefined' && document.title
        ? document.title.replace(/\s*[|·—-]\s*CofounderHubs.*$/i, '').trim()
        : null) || getPageLabel(landingPage);

    const message = formatVisitorMessage({
      client,
      geo,
      page: pageLabel,
      path: landingPage,
      username,
      extra: `referrer: ${referrer || 'direct'} (${visitType || 'external'})`,
    });

    await sendTelegramMessage(message);
  } catch {
    // Never throw
  }
};
